<script>
import { mapGetters } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import NextButton from 'dashboard/components-next/button/Button.vue';
import googleAdsAPI from 'dashboard/api/googleAds';
import ga4API from 'dashboard/api/ga4';
import searchConsoleAPI from 'dashboard/api/searchConsole';
import metaAdsAPI from 'dashboard/api/metaAds';
import tiktokAdsAPI from 'dashboard/api/tiktokAds';
import { setupFacebookSdk, launchMetaAdsLogin } from '../utils';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      connections: [],
      loading: false,
      connectingGoogle: false,
      connectingMeta: false,
      testingId: null,
      customersByConn: {},
      ga4PropsByConn: {},
      scSitesByConn: {},
      savedId: null,
      popupTimer: null,
      tiktokConnections: [],
      connectingTiktok: false,
      tiktokPopupTimer: null,
      googlePopup: null,
      tiktokPopup: null,
      tiktokAdvertisersByConn: {},
      // Meta: gerenciador por LOGIN x servico (pages/accounts/pixels),
      // chaveado por `${login.key}-${svc}`.
      metaMgrOpen: null,
      metaMgrLoading: null,
      metaMgrItems: {},
      metaMgrSelected: {},
      // Google: um gerenciador multi-selecao por servico (ads/ga4/sc),
      // chaveado por `${conn.id}-${resource}`.
      googleMgrOpen: null,
      googleMgrLoading: null,
      googleSelected: {},
      // TikTok: gerenciador de anunciantes (multiplos) por conexao
      tiktokMgrOpen: null,
      tiktokMgrLoading: null,
      tiktokSelected: {},
    };
  },
  computed: {
    ...mapGetters({
      metaConnections: 'metaAds/getConnections',
      metaUiFlags: 'metaAds/getUIFlags',
      capiPixels: 'metaCapiPixels/getPixels',
    }),
    // Conexoes Meta agrupadas por LOGIN do Facebook (paginas do mesmo login
    // compartilham o meta_user_id) — o card e por login, igual ao Google.
    metaLogins() {
      const groups = {};
      (this.metaConnections || []).forEach(conn => {
        const key = conn.meta_user_id || `conn-${conn.id}`;
        if (!groups[key]) groups[key] = { key, connections: [] };
        groups[key].connections.push(conn);
      });
      return Object.values(groups);
    },
    // Os 3 servicos do login Meta, mesmo padrao do Google.
    metaServices() {
      return [
        {
          key: 'pages',
          labelKey: 'META_ADS.CONNECTIONS.META.PAGES',
          icon: 'i-lucide-flag',
        },
        {
          key: 'accounts',
          labelKey: 'META_ADS.CONNECTIONS.META.AD_ACCOUNTS',
          icon: 'i-lucide-megaphone',
        },
        {
          key: 'pixels',
          labelKey: 'META_ADS.CONNECTIONS.META.PIXELS',
          icon: 'i-lucide-radar',
        },
      ];
    },
    // Os 3 servicos do login Google, todos com o MESMO padrao de escolha
    // (Gerenciar -> checkboxes multi + chips). Relatorios mostram tudo marcado.
    googleResources() {
      return [
        {
          key: 'ads',
          field: 'selected_ad_accounts',
          labelKey: 'META_ADS.CONNECTIONS.GOOGLE.ADS_ACCOUNTS',
          icon: 'i-lucide-megaphone',
        },
        {
          key: 'ga4',
          field: 'selected_ga4_properties',
          labelKey: 'META_ADS.CONNECTIONS.GOOGLE.GA4_PROPERTIES',
          icon: 'i-lucide-bar-chart-3',
        },
        {
          key: 'sc',
          field: 'selected_sc_sites',
          labelKey: 'META_ADS.CONNECTIONS.GOOGLE.SC_SITES',
          icon: 'i-lucide-globe',
        },
      ];
    },
  },
  mounted() {
    this.fetchConnections();
    this.$store.dispatch('metaAds/get');
    this.$store.dispatch('metaCapiPixels/get');
    this.fetchTiktokConnections();
    window.addEventListener('storage', this.handleStorageSignal);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageSignal);
    if (this.popupTimer) clearInterval(this.popupTimer);
    if (this.tiktokPopupTimer) clearInterval(this.tiktokPopupTimer);
  },
  methods: {
    // ── Meta: gerenciador por login x servico (pages/accounts/pixels) ──
    metaKey(login, svc) {
      return `${login.key}-${svc}`;
    },
    metaPrimaryConn(login) {
      return login.connections[0];
    },
    metaStatus(login) {
      return login.connections.some(c => c.status === 'active')
        ? 'active'
        : login.connections[0].status;
    },
    // Identidade do card: nomes das paginas do login (e o que o usuario
    // reconhece — o meta_user_id sozinho nao diz nada).
    metaLoginTitle(login) {
      const names = login.connections.map(c => c.name).filter(Boolean);
      if (!names.length) return login.key;
      return names.length > 2
        ? `${names.slice(0, 2).join(', ')} +${names.length - 2}`
        : names.join(', ');
    },
    // Chips (fechado): o que esta LIGADO em cada servico do login.
    metaActiveChips(login, svc) {
      if (svc === 'pages') {
        return login.connections
          .filter(c => c.leadgen_subscribed)
          .map(c => ({ id: `${c.id}`, name: c.name }));
      }
      if (svc === 'accounts') {
        const seen = new Set();
        return login.connections
          .flatMap(c => c.selected_ad_accounts || [])
          .filter(a => a.id && !seen.has(a.id) && seen.add(a.id))
          .map(a => ({ id: a.id, name: a.name || a.id }));
      }
      return (this.capiPixels || [])
        .filter(p => p.enabled && p.meta_user_id === login.key)
        .map(p => ({ id: p.pixel_id, name: p.name }));
    },
    async loadMetaItems(login, svc) {
      if (svc === 'pages') {
        return login.connections.map(c => ({
          id: `${c.id}`,
          name: c.name,
          sub: c.page_id,
        }));
      }
      if (svc === 'accounts') {
        // contas por conexao do login, com dono marcado (salva no dono certo)
        const seen = new Set();
        const items = [];
        await Promise.all(
          login.connections.map(async conn => {
            try {
              const { data } = await metaAdsAPI.adAccounts(conn.id);
              (Array.isArray(data) ? data : []).forEach(acc => {
                if (seen.has(acc.id)) return;
                seen.add(acc.id);
                items.push({ ...acc, connId: conn.id });
              });
            } catch (e) {
              // conexao sem acesso a contas: segue com as demais
            }
          })
        );
        return items;
      }
      const { data } = await metaAdsAPI.pixels(this.metaPrimaryConn(login).id);
      return Array.isArray(data) ? data : [];
    },
    metaSeed(login, svc, items) {
      if (svc === 'pages') {
        return login.connections
          .filter(c => c.leadgen_subscribed)
          .map(c => `${c.id}`);
      }
      if (svc === 'accounts') {
        return this.metaActiveChips(login, 'accounts').map(a => `${a.id}`);
      }
      return items.filter(px => px.enabled).map(px => `${px.id}`);
    },
    async toggleMetaMgr(login, svc) {
      const key = this.metaKey(login, svc);
      if (this.metaMgrOpen === key) {
        // fechar = salvar a selecao
        await this.saveMetaSelection(login, svc);
        this.metaMgrOpen = null;
        return;
      }
      // abrir = lista fresca + semente do que esta ligado
      this.metaMgrLoading = key;
      try {
        const items = await this.loadMetaItems(login, svc);
        this.metaMgrItems = { ...this.metaMgrItems, [key]: items };
        this.metaMgrSelected = {
          ...this.metaMgrSelected,
          [key]: this.metaSeed(login, svc, items),
        };
        this.metaMgrOpen = key;
      } catch (e) {
        toast.error(this.$t('META_ADS.CONNECTIONS.META.ACCOUNTS_ERROR'));
      } finally {
        this.metaMgrLoading = null;
      }
    },
    isMetaItemSelected(login, svc, item) {
      return (this.metaMgrSelected[this.metaKey(login, svc)] || []).includes(
        `${item.id}`
      );
    },
    toggleMetaItem(login, svc, item) {
      const key = this.metaKey(login, svc);
      const current = this.metaMgrSelected[key] || [];
      const id = `${item.id}`;
      const next = current.includes(id)
        ? current.filter(a => a !== id)
        : [...current, id];
      this.metaMgrSelected = { ...this.metaMgrSelected, [key]: next };
    },
    async saveMetaSelection(login, svc) {
      const key = this.metaKey(login, svc);
      const selected = this.metaMgrSelected[key] || [];
      const items = this.metaMgrItems[key] || [];
      try {
        if (svc === 'pages') {
          // marcar pagina = assinar Lead Ads; desmarcar = parar de coletar
          await Promise.all(
            login.connections
              .filter(
                c => c.leadgen_subscribed !== selected.includes(`${c.id}`)
              )
              .map(c =>
                metaAdsAPI.saveLeadgen(c.id, selected.includes(`${c.id}`))
              )
          );
        } else if (svc === 'accounts') {
          // cada conta e salva na conexao DONA dela; quem nao tem nada, limpa
          const byConn = {};
          items.forEach(acc => {
            if (!selected.includes(`${acc.id}`)) return;
            if (!byConn[acc.connId]) byConn[acc.connId] = [];
            byConn[acc.connId].push({
              id: acc.id,
              act_id: acc.act_id,
              name: acc.name,
              currency: acc.currency,
            });
          });
          // conta marcada que nao apareceu na lista ao vivo (falha da API):
          // preserva na conexao que ja a guarda, em vez de derrubar
          const liveIds = new Set(items.map(acc => `${acc.id}`));
          login.connections.forEach(conn => {
            (conn.selected_ad_accounts || []).forEach(acc => {
              if (selected.includes(`${acc.id}`) && !liveIds.has(`${acc.id}`)) {
                if (!byConn[conn.id]) byConn[conn.id] = [];
                byConn[conn.id].push(acc);
              }
            });
          });
          await Promise.all(
            login.connections.map(conn =>
              metaAdsAPI.saveAdAccounts(conn.id, byConn[conn.id] || [])
            )
          );
        } else {
          const primary = this.metaPrimaryConn(login);
          // TODOS os marcados sao enviados (idempotente): um pixel manual
          // pre-existente ja vem "enabled" mas SEM vinculo com o login — o
          // diff so-de-enabled pulava ele e o chip nunca aparecia.
          const toEnable = items.filter(px => selected.includes(`${px.id}`));
          const toDisable = items.filter(
            px => px.enabled && !selected.includes(`${px.id}`)
          );
          await Promise.all([
            ...toEnable.map(px => metaAdsAPI.togglePixel(primary.id, px, true)),
            ...toDisable.map(px =>
              metaAdsAPI.togglePixel(primary.id, px, false)
            ),
          ]);
          await this.$store.dispatch('metaCapiPixels/get');
        }
        await this.$store.dispatch('metaAds/get');
        toast.success(this.$t('META_ADS.CONNECTIONS.META.ACCOUNTS_SAVED'));
      } catch (e) {
        toast.error(this.$t('META_ADS.CONNECTIONS.META.ACCOUNTS_ERROR'));
      }
    },
    async removeMetaLogin(login) {
      // remover o login = remover todas as paginas/conexoes dele
      try {
        await Promise.all(
          login.connections.map(c =>
            this.$store.dispatch('metaAds/delete', c.id)
          )
        );
        toast.success(this.$t('META_ADS.LEAD_ADS.DELETE.SUCCESS'));
      } catch (error) {
        toast.error(this.$t('META_ADS.LEAD_ADS.DELETE.ERROR'));
      }
    },
    // ── Google: gerenciador multi-selecao por servico (ads/ga4/sc) ──
    mgrKey(conn, resource) {
      return `${conn.id}-${resource}`;
    },
    googleResourceField(resource) {
      return {
        ads: 'selected_ad_accounts',
        ga4: 'selected_ga4_properties',
        sc: 'selected_sc_sites',
      }[resource];
    },
    // Itens disponiveis normalizados {id, name}, por servico.
    googleItems(conn, resource) {
      if (resource === 'ads') {
        return (this.customersByConn[conn.id] || []).map(c => ({
          id: c.customer_id,
          name: c.name || c.customer_id,
        }));
      }
      if (resource === 'ga4') {
        return (this.ga4PropsByConn[conn.id] || []).map(p => ({
          id: p.property_id,
          name: p.display_name || p.property_id,
        }));
      }
      return (this.scSitesByConn[conn.id] || []).map(s => ({
        id: s.site_url,
        name: s.site_url,
      }));
    },
    async toggleGoogleMgr(conn, resource) {
      const key = this.mgrKey(conn, resource);
      if (this.googleMgrOpen === key) {
        // fechar = salvar a selecao
        await this.saveGoogleSelection(conn, resource);
        this.googleMgrOpen = null;
        return;
      }
      // abrir = lista fresca + semente da selecao salva
      this.googleMgrLoading = key;
      this.googleSelected = {
        ...this.googleSelected,
        [key]: [...(conn[this.googleResourceField(resource)] || [])],
      };
      try {
        await this.loadResource(conn, resource);
        this.googleMgrOpen = key;
      } finally {
        this.googleMgrLoading = null;
      }
    },
    isGoogleItemSelected(conn, resource, item) {
      return (this.googleSelected[this.mgrKey(conn, resource)] || []).some(
        a => a.id === item.id
      );
    },
    toggleGoogleItem(conn, resource, item) {
      const key = this.mgrKey(conn, resource);
      const current = this.googleSelected[key] || [];
      const exists = current.some(a => a.id === item.id);
      const next = exists
        ? current.filter(a => a.id !== item.id)
        : [...current, { id: item.id, name: item.name }];
      this.googleSelected = { ...this.googleSelected, [key]: next };
    },
    async saveGoogleSelection(conn, resource) {
      try {
        await googleAdsAPI.update(conn.id, {
          [this.googleResourceField(resource)]:
            this.googleSelected[this.mgrKey(conn, resource)] || [],
        });
        await this.reloadConnections();
        this.flagSaved(conn.id);
      } catch (e) {
        toast.error(this.$t('META_ADS.CONNECTIONS.GOOGLE.ACCOUNTS_ERROR'));
      }
    },
    // ── Google ──────────────────────────────────────────────
    connectionTitle(conn) {
      return (
        conn.name ||
        conn.customer_id ||
        this.$t('META_ADS.CONNECTIONS.GOOGLE.NEW')
      );
    },
    onGoogleConnected() {
      this.connectingGoogle = false;
      toast.success(this.$t('META_ADS.CONVERSIONS.CONNECTED'));
      this.fetchConnections();
    },
    handleStorageSignal(event) {
      if (!event.newValue) return;
      if (event.key === 'google_ads_oauth') {
        this.closePopup('googlePopup');
        if (event.newValue.startsWith('GOOGLE_ADS_CONNECTED')) {
          this.onGoogleConnected();
        } else {
          this.connectingGoogle = false;
        }
      } else if (event.key === 'tiktok_ads_oauth') {
        this.closePopup('tiktokPopup');
        this.connectingTiktok = false;
        if (event.newValue.startsWith('TIKTOK_ADS_CONNECTED')) {
          this.fetchTiktokConnections();
        }
      }
    },
    closePopup(ref) {
      try {
        if (this[ref] && !this[ref].closed) this[ref].close();
      } catch (error) {
        // popup may already be closed or cross-origin — ignore
      }
      this[ref] = null;
    },
    openOAuthPopup(url) {
      const width = 600;
      const height = 720;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        url,
        'google_ads_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      this.googlePopup = popup;
      if (this.popupTimer) clearInterval(this.popupTimer);
      this.popupTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(this.popupTimer);
          this.connectingGoogle = false;
          this.fetchConnections();
        }
      }, 800);
    },
    async fetchConnections() {
      this.loading = true;
      try {
        const { data } = await googleAdsAPI.get();
        this.connections = data;
      } finally {
        this.loading = false;
      }
    },
    async reloadConnections() {
      const { data } = await googleAdsAPI.get();
      this.connections = data;
    },
    async connectGoogle() {
      this.connectingGoogle = true;
      try {
        const { data } = await googleAdsAPI.generateAuthorization();
        this.openOAuthPopup(data.url);
      } catch (error) {
        this.connectingGoogle = false;
        toast.error(this.$t('META_ADS.CONVERSIONS.CONNECT_ERROR'));
      }
    },
    async loadCustomers(id) {
      try {
        const { data } = await googleAdsAPI.customers(id);
        this.customersByConn = { ...this.customersByConn, [id]: data };
      } catch (error) {
        this.customersByConn = { ...this.customersByConn, [id]: [] };
      }
    },
    async loadGa4Properties(id) {
      try {
        const { data } = await ga4API.properties(id);
        this.ga4PropsByConn = {
          ...this.ga4PropsByConn,
          [id]: data.data || [],
        };
      } catch (error) {
        this.ga4PropsByConn = { ...this.ga4PropsByConn, [id]: [] };
      }
    },
    async loadScSites(id) {
      try {
        const { data } = await searchConsoleAPI.sites(id);
        this.scSitesByConn = { ...this.scSitesByConn, [id]: data.data || [] };
      } catch (error) {
        this.scSitesByConn = { ...this.scSitesByConn, [id]: [] };
      }
    },
    loadResource(conn, resource) {
      if (resource === 'ads') return this.loadCustomers(conn.id);
      if (resource === 'ga4') return this.loadGa4Properties(conn.id);
      return this.loadScSites(conn.id);
    },
    flagSaved(id) {
      this.savedId = id;
      setTimeout(() => {
        if (this.savedId === id) this.savedId = null;
      }, 2500);
    },
    async testGoogle(conn) {
      this.testingId = conn.id;
      try {
        await googleAdsAPI.test(conn.id);
        toast.success(this.$t('META_ADS.CONVERSIONS.TEST.SUCCESS'));
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.error) ||
          '';
        toast.error(
          `${this.$t('META_ADS.CONVERSIONS.TEST.ERROR')} ${message}`.trim()
        );
      } finally {
        this.testingId = null;
      }
    },
    async removeGoogle(id) {
      try {
        await googleAdsAPI.delete(id);
        this.fetchConnections();
      } catch (error) {
        toast.error(this.$t('META_ADS.CONVERSIONS.SAVE_ERROR'));
      }
    },
    // ── Meta ────────────────────────────────────────────────
    metaStatusLabel(status) {
      const labels = {
        active: this.$t('META_ADS.LEAD_ADS.STATUS.ACTIVE'),
        disconnected: this.$t('META_ADS.LEAD_ADS.STATUS.DISCONNECTED'),
        error: this.$t('META_ADS.LEAD_ADS.STATUS.ERROR'),
      };
      return labels[status] || labels.active;
    },
    async connectMeta() {
      const appId = window.chatwootConfig?.metaAdsAppId;
      const configId = window.chatwootConfig?.metaAdsConfigurationId;
      const apiVersion = window.chatwootConfig?.fbApiVersion;
      if (!appId || !configId) {
        this.redirectConnectMeta();
        return;
      }
      this.connectingMeta = true;
      try {
        await setupFacebookSdk(appId, apiVersion);
        const code = await launchMetaAdsLogin(configId);
        await this.$store.dispatch('metaAds/connect', code);
        toast.success(this.$t('META_ADS.LEAD_ADS.AUTHORIZATION.SUCCESS'));
      } catch (error) {
        if (error.message !== 'Login cancelled') {
          toast.error(this.$t('META_ADS.LEAD_ADS.AUTHORIZATION.ERROR'));
        }
      } finally {
        this.connectingMeta = false;
      }
    },
    async redirectConnectMeta() {
      this.connectingMeta = true;
      try {
        const { data } = await metaAdsAPI.generateAuthorization();
        window.location.href = data.url;
      } catch (error) {
        this.connectingMeta = false;
        toast.error(this.$t('META_ADS.LEAD_ADS.AUTHORIZATION.ERROR'));
      }
    },
    async fetchTiktokConnections() {
      try {
        const { data } = await tiktokAdsAPI.get();
        this.tiktokConnections = data || [];
        this.tiktokConnections.forEach(c => this.loadTiktokAdvertisers(c.id));
      } catch (error) {
        this.tiktokConnections = [];
      }
    },
    async loadTiktokAdvertisers(id) {
      try {
        const { data } = await tiktokAdsAPI.advertisers(id);
        this.tiktokAdvertisersByConn = {
          ...this.tiktokAdvertisersByConn,
          [id]: data || [],
        };
      } catch (error) {
        this.tiktokAdvertisersByConn = {
          ...this.tiktokAdvertisersByConn,
          [id]: [],
        };
      }
    },
    // ── TikTok: anunciantes (multiplos por conexao), mesmo padrao ───
    tiktokAdvName(conn, id) {
      const adv = (this.tiktokAdvertisersByConn[conn.id] || []).find(
        a => `${a.id}` === `${id}`
      );
      return adv ? adv.name : id;
    },
    async toggleTiktokMgr(conn) {
      if (this.tiktokMgrOpen === conn.id) {
        // fechar = salvar a selecao
        await this.saveTiktokSelection(conn);
        this.tiktokMgrOpen = null;
        return;
      }
      // abrir = lista fresca + semente da selecao salva
      this.tiktokMgrLoading = conn.id;
      this.tiktokSelected = {
        ...this.tiktokSelected,
        [conn.id]: [...(conn.advertiser_ids || []).map(String)],
      };
      try {
        await this.loadTiktokAdvertisers(conn.id);
        this.tiktokMgrOpen = conn.id;
      } finally {
        this.tiktokMgrLoading = null;
      }
    },
    isTiktokAdvSelected(conn, adv) {
      return (this.tiktokSelected[conn.id] || []).includes(`${adv.id}`);
    },
    toggleTiktokAdv(conn, adv) {
      const current = this.tiktokSelected[conn.id] || [];
      const id = `${adv.id}`;
      const next = current.includes(id)
        ? current.filter(a => a !== id)
        : [...current, id];
      this.tiktokSelected = { ...this.tiktokSelected, [conn.id]: next };
    },
    async saveTiktokSelection(conn) {
      try {
        await tiktokAdsAPI.update(conn.id, this.tiktokSelected[conn.id] || []);
        await this.fetchTiktokConnections();
        toast.success(this.$t('META_ADS.CONNECTIONS.TIKTOK.ACCOUNT_SAVED'));
      } catch (error) {
        toast.error(this.$t('META_ADS.CONNECTIONS.TIKTOK.ERROR'));
      }
    },
    async connectTiktok() {
      this.connectingTiktok = true;
      try {
        const { data } = await tiktokAdsAPI.generateAuthorization();
        this.openTiktokPopup(data.url);
      } catch (error) {
        this.connectingTiktok = false;
        toast.error(this.$t('META_ADS.CONNECTIONS.TIKTOK.ERROR'));
      }
    },
    openTiktokPopup(url) {
      const width = 600;
      const height = 720;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        url,
        'tiktok_ads_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      this.tiktokPopup = popup;
      if (this.tiktokPopupTimer) clearInterval(this.tiktokPopupTimer);
      this.tiktokPopupTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(this.tiktokPopupTimer);
          this.connectingTiktok = false;
          this.fetchTiktokConnections();
        }
      }, 800);
    },
    async removeTiktok(id) {
      try {
        await tiktokAdsAPI.delete(id);
        this.fetchTiktokConnections();
      } catch (error) {
        toast.error(this.$t('META_ADS.CONNECTIONS.TIKTOK.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('META_ADS.CONNECTIONS.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('META_ADS.CONNECTIONS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Google -->
    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-chrome text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.ENABLES') }}
            </p>
          </div>
        </div>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.CONNECTIONS.GOOGLE.CONNECT')"
          :is-loading="connectingGoogle"
          sm
          class="shrink-0"
          @click="connectGoogle"
        />
      </div>

      <div
        v-if="!loading && !connections.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        {{ $t('META_ADS.CONNECTIONS.GOOGLE.EMPTY') }}
      </div>

      <div
        v-for="conn in connections"
        :key="conn.id"
        class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <p class="font-medium truncate text-n-slate-12">
              {{ connectionTitle(conn) }}
            </p>
            <span
              v-if="savedId === conn.id"
              class="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 shrink-0"
            >
              <span class="i-lucide-check" />
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.SAVED') }}
            </span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <NextButton
              v-if="conn.customer_id"
              icon="i-lucide-badge-check"
              :is-loading="testingId === conn.id"
              slate
              faded
              xs
              @click="testGoogle(conn)"
            />
            <NextButton
              icon="i-lucide-trash-2"
              ruby
              faded
              xs
              @click="removeGoogle(conn.id)"
            />
          </div>
        </div>

        <!-- Um padrao so por servico: Gerenciar -> checkboxes multi + chips.
             Tudo marcado aqui alimenta os relatorios; la nao se escolhe nada.
             Conta de conversao offline e escolhida no proprio workflow. -->
        <div
          v-for="res in googleResources"
          :key="res.key"
          class="pt-3 border-t border-n-weak"
        >
          <div class="flex items-center justify-between gap-2 mb-2">
            <span class="text-xs font-semibold text-n-slate-11">
              {{ $t(res.labelKey) }}
            </span>
            <NextButton
              :label="
                googleMgrOpen === mgrKey(conn, res.key)
                  ? $t('META_ADS.CONNECTIONS.GOOGLE.DONE')
                  : $t('META_ADS.CONNECTIONS.GOOGLE.MANAGE_ACCOUNTS')
              "
              :icon="
                googleMgrOpen === mgrKey(conn, res.key)
                  ? 'i-lucide-check'
                  : 'i-lucide-settings-2'
              "
              slate
              faded
              xs
              :is-loading="googleMgrLoading === mgrKey(conn, res.key)"
              @click="toggleGoogleMgr(conn, res.key)"
            />
          </div>

          <!-- gerenciador aberto: checkboxes do que o login enxerga -->
          <div
            v-if="googleMgrOpen === mgrKey(conn, res.key)"
            class="flex flex-col gap-1.5"
          >
            <p
              v-if="!googleItems(conn, res.key).length"
              class="text-xs text-n-slate-10"
            >
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.NO_ACCOUNTS') }}
            </p>
            <label
              v-for="item in googleItems(conn, res.key)"
              :key="item.id"
              class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-2"
            >
              <input
                type="checkbox"
                class="!mb-0 !mt-0"
                :checked="isGoogleItemSelected(conn, res.key, item)"
                @change="toggleGoogleItem(conn, res.key, item)"
              />
              <span class="text-sm text-n-slate-12">{{ item.name }}</span>
              <span
                v-if="item.name !== item.id"
                class="text-[10px] text-n-slate-9"
              >
                {{ item.id }}
              </span>
            </label>
          </div>

          <!-- fechado: chips do que esta ligado -->
          <div v-else class="flex flex-wrap gap-1.5">
            <span
              v-for="item in conn[res.field] || []"
              :key="item.id"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-alpha-2 text-n-slate-11"
            >
              <span class="size-3" :class="res.icon" />
              {{ item.name || item.id }}
            </span>
            <span
              v-if="!(conn[res.field] || []).length"
              class="text-xs italic text-n-amber-11"
            >
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.NONE_SELECTED') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Meta: um card por LOGIN do Facebook, 3 servicos no mesmo padrao.
         A pagina ENTRA pelo popup do FB (regra da Meta); aqui se marca o que
         cada login coleta: paginas (Lead Ads), contas de anuncio e pixels. -->
    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-facebook text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('META_ADS.CONNECTIONS.META.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('META_ADS.CONNECTIONS.META.ENABLES') }}
            </p>
          </div>
        </div>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.CONNECTIONS.META.CONNECT')"
          :is-loading="connectingMeta"
          sm
          class="shrink-0"
          @click="connectMeta"
        />
      </div>

      <div
        v-if="!metaUiFlags.fetchingList && !metaConnections.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        {{ $t('META_ADS.CONNECTIONS.META.EMPTY') }}
      </div>

      <div
        v-for="login in metaLogins"
        :key="login.key"
        class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="font-medium truncate text-n-slate-12">
              {{ metaLoginTitle(login) }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{
                $t('META_ADS.CONNECTIONS.META.LOGIN_SUBTITLE', {
                  count: login.connections.length,
                })
              }}
            </p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                metaStatus(login) === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              "
            >
              {{ metaStatusLabel(metaStatus(login)) }}
            </span>
            <NextButton
              icon="i-lucide-trash-2"
              ruby
              faded
              xs
              @click="removeMetaLogin(login)"
            />
          </div>
        </div>

        <div
          v-for="svc in metaServices"
          :key="svc.key"
          class="pt-3 border-t border-n-weak"
        >
          <div class="flex items-center justify-between gap-2 mb-2">
            <span class="text-xs font-semibold text-n-slate-11">
              {{ $t(svc.labelKey) }}
            </span>
            <NextButton
              :label="
                metaMgrOpen === metaKey(login, svc.key)
                  ? $t('META_ADS.CONNECTIONS.META.DONE')
                  : $t('META_ADS.CONNECTIONS.META.MANAGE_ACCOUNTS')
              "
              :icon="
                metaMgrOpen === metaKey(login, svc.key)
                  ? 'i-lucide-check'
                  : 'i-lucide-settings-2'
              "
              slate
              faded
              xs
              :is-loading="metaMgrLoading === metaKey(login, svc.key)"
              @click="toggleMetaMgr(login, svc.key)"
            />
          </div>

          <!-- gerenciador aberto: checkboxes do que o login enxerga -->
          <div
            v-if="metaMgrOpen === metaKey(login, svc.key)"
            class="flex flex-col gap-1.5"
          >
            <p
              v-if="!(metaMgrItems[metaKey(login, svc.key)] || []).length"
              class="text-xs text-n-slate-10"
            >
              {{ $t('META_ADS.CONNECTIONS.META.NO_ACCOUNTS') }}
            </p>
            <label
              v-for="item in metaMgrItems[metaKey(login, svc.key)] || []"
              :key="item.id"
              class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-2"
            >
              <input
                type="checkbox"
                class="!mb-0 !mt-0"
                :checked="isMetaItemSelected(login, svc.key, item)"
                @change="toggleMetaItem(login, svc.key, item)"
              />
              <span class="text-sm text-n-slate-12">{{ item.name }}</span>
              <span
                v-if="item.sub || `${item.name}` !== `${item.id}`"
                class="text-[10px] text-n-slate-9"
              >
                {{ item.sub || item.id }}
              </span>
            </label>
          </div>

          <!-- fechado: chips do que esta ligado -->
          <div v-else class="flex flex-wrap gap-1.5">
            <span
              v-for="item in metaActiveChips(login, svc.key)"
              :key="item.id"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-alpha-2 text-n-slate-11"
            >
              <span class="size-3" :class="svc.icon" />
              {{ item.name || item.id }}
            </span>
            <span
              v-if="!metaActiveChips(login, svc.key).length"
              class="text-xs italic text-n-amber-11"
            >
              {{ $t('META_ADS.CONNECTIONS.META.NONE_SELECTED') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-music text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('META_ADS.CONNECTIONS.TIKTOK.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('META_ADS.CONNECTIONS.TIKTOK.ENABLES') }}
            </p>
          </div>
        </div>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.CONNECTIONS.TIKTOK.CONNECT')"
          :is-loading="connectingTiktok"
          sm
          class="shrink-0"
          @click="connectTiktok"
        />
      </div>

      <div
        v-if="!tiktokConnections.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        {{ $t('META_ADS.CONNECTIONS.TIKTOK.EMPTY') }}
      </div>

      <div
        v-for="conn in tiktokConnections"
        :key="conn.id"
        class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="flex items-center justify-between gap-3">
          <p class="font-medium truncate text-n-slate-12">{{ conn.name }}</p>
          <div class="flex items-center gap-2 shrink-0">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                conn.status === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              "
            >
              {{ metaStatusLabel(conn.status) }}
            </span>
            <NextButton
              icon="i-lucide-trash-2"
              ruby
              faded
              xs
              @click="removeTiktok(conn.id)"
            />
          </div>
        </div>

        <!-- Anunciantes: mesmo padrao dos outros cards (multi + chips) -->
        <div class="pt-3 border-t border-n-weak">
          <div class="flex items-center justify-between gap-2 mb-2">
            <span class="text-xs font-semibold text-n-slate-11">
              {{ $t('META_ADS.CONNECTIONS.TIKTOK.AD_ACCOUNTS') }}
            </span>
            <NextButton
              :label="
                tiktokMgrOpen === conn.id
                  ? $t('META_ADS.CONNECTIONS.GOOGLE.DONE')
                  : $t('META_ADS.CONNECTIONS.GOOGLE.MANAGE_ACCOUNTS')
              "
              :icon="
                tiktokMgrOpen === conn.id
                  ? 'i-lucide-check'
                  : 'i-lucide-settings-2'
              "
              slate
              faded
              xs
              :is-loading="tiktokMgrLoading === conn.id"
              @click="toggleTiktokMgr(conn)"
            />
          </div>

          <!-- gerenciador aberto: checkboxes dos anunciantes do token -->
          <div v-if="tiktokMgrOpen === conn.id" class="flex flex-col gap-1.5">
            <p
              v-if="!(tiktokAdvertisersByConn[conn.id] || []).length"
              class="text-xs text-n-slate-10"
            >
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.NO_ACCOUNTS') }}
            </p>
            <label
              v-for="adv in tiktokAdvertisersByConn[conn.id] || []"
              :key="adv.id"
              class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-2"
            >
              <input
                type="checkbox"
                class="!mb-0 !mt-0"
                :checked="isTiktokAdvSelected(conn, adv)"
                @change="toggleTiktokAdv(conn, adv)"
              />
              <span class="text-sm text-n-slate-12">{{ adv.name }}</span>
              <span class="text-[10px] text-n-slate-9">{{ adv.id }}</span>
            </label>
          </div>

          <!-- fechado: chips do que esta ligado -->
          <div v-else class="flex flex-wrap gap-1.5">
            <span
              v-for="id in conn.advertiser_ids || []"
              :key="id"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-alpha-2 text-n-slate-11"
            >
              <span class="i-lucide-megaphone size-3" />
              {{ tiktokAdvName(conn, id) }}
            </span>
            <span
              v-if="!(conn.advertiser_ids || []).length"
              class="text-xs italic text-n-amber-11"
            >
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.NONE_SELECTED') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
